import { createContext, createElement, useContext, type PropsWithChildren } from 'react';

import type { AuthEnvironment } from '../auth/types';

export const capabilityIds = [
  'authProfile',
  'dashboard',
  'partners',
  'agents',
  'adminUsers',
  'members',
  'games',
  'commissionRules',
  'transactions',
  'transactionReversal',
  'transactionFailures',
  'apiOperations',
  'commissionLedger',
  'commissionAdjustments',
  'audit',
  'securityEvents',
  'wallets',
  'withdrawals',
  'withdrawalControls',
  'partnerCredit',
  'partnerCreditHealth',
  'approvals',
  'reconciliation',
  'reportExports',
  'reports',
] as const;

export type CapabilityId = (typeof capabilityIds)[number];
export type DataSource = 'real' | 'mock' | 'unavailable';

const CapabilityOverridesContext = createContext<
  Partial<Readonly<Record<CapabilityId, DataSource>>>
>({});

export function CapabilityProvider({
  children,
  overrides = {},
}: PropsWithChildren<{
  readonly overrides?: Partial<Readonly<Record<CapabilityId, DataSource>>>;
}>) {
  return createElement(CapabilityOverridesContext.Provider, { value: overrides }, children);
}

const backendGaps = new Set<CapabilityId>([
  'transactionReversal',
  'transactionFailures',
  'apiOperations',
  'commissionAdjustments',
  'partnerCreditHealth',
]);

export function resolveDataSource(
  capability: CapabilityId,
  environment: AuthEnvironment,
): DataSource {
  if (environment.authMode === 'mock') {
    if (environment.environment !== 'dev') throw new Error('Mock Auth is DEV-only');
    return 'mock';
  }

  return backendGaps.has(capability) ? 'unavailable' : 'real';
}

export function useDataSource(capability: CapabilityId, environment: AuthEnvironment): DataSource {
  const overrides = useContext(CapabilityOverridesContext);
  return overrides[capability] ?? resolveDataSource(capability, environment);
}
